import type { ConversaDetalheDTO } from '@viniko/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, useToast } from '../../componentes/ui';
import { api, patch, post } from '../../lib/api';
import { chaves, useEtiquetas, type Etiqueta } from '../../lib/consultas';

function useAtualizar(conversaId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: chaves.conversa(conversaId) });
    qc.invalidateQueries({ queryKey: ['conversas'] });
  };
}

export function ModalEditarContato({
  conversa: c,
  fechar,
}: {
  conversa: ConversaDetalheDTO;
  fechar: () => void;
}) {
  const toast = useToast();
  const atualizar = useAtualizar(c.id);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    try {
      await patch(`/api/contatos/${c.contato.id}`, {
        nome: d.nome,
        empresa: d.empresa,
        cidade: d.cidade,
      });
      toast('Contato atualizado');
      atualizar();
      fechar();
    } catch (err) {
      setErro((err as Error).message);
    }
  }

  return (
    <Modal titulo="Editar contato" aoFechar={fechar}>
      <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label className="campo">
          <span>Nome</span>
          <input
            id="contato-nome"
            name="nome"
            className="input"
            defaultValue={c.contato.nome}
            required
            maxLength={120}
          />
        </label>
        <label className="campo">
          <span>Empresa</span>
          <input
            id="contato-empresa"
            name="empresa"
            className="input"
            defaultValue={c.contato.empresa ?? ''}
            maxLength={120}
          />
        </label>
        <label className="campo">
          <span>Cidade</span>
          <input
            id="contato-cidade"
            name="cidade"
            className="input"
            defaultValue={c.contato.cidade ?? ''}
            placeholder="Ex: Porto Alegre/RS"
            maxLength={120}
          />
          <small>
            Muda só na central. O cadastro oficial (CPF/CNPJ, endereço) é o do Tiny, na Fase 5.
          </small>
        </label>
        {erro && <div className="erro-form">{erro}</div>}
        <div className="acoes">
          <button type="button" className="btn" onClick={fechar}>
            Cancelar
          </button>
          <button className="btn primario">Salvar</button>
        </div>
      </form>
    </Modal>
  );
}

const CORES_ETIQUETA = [
  '#001b82',
  '#1daa61',
  '#c2362b',
  '#a86300',
  '#7a3fd1',
  '#0b6e99',
  '#b8326e',
  '#5d6678',
];

export function ModalEtiquetas({
  conversa: c,
  fechar,
}: {
  conversa: ConversaDetalheDTO;
  fechar: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const atualizar = useAtualizar(c.id);
  const todas = useEtiquetas().data ?? [];
  const [marcadas, setMarcadas] = useState(() => new Set(c.etiquetas.map((e) => e.id)));
  const [nova, setNova] = useState('');
  const [cor, setCor] = useState(CORES_ETIQUETA[0]!);
  const [salvando, setSalvando] = useState(false);

  const alternar = (id: string) =>
    setMarcadas((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  async function criar() {
    if (!nova.trim()) return;
    try {
      const t = await post<Etiqueta>('/api/etiquetas', { nome: nova.trim(), cor });
      await qc.invalidateQueries({ queryKey: ['etiquetas'] });
      setMarcadas((s) => new Set(s).add(t.id));
      setNova('');
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  async function salvar() {
    setSalvando(true);
    try {
      await api(`/api/conversas/${c.id}/etiquetas`, {
        metodo: 'PUT',
        corpo: { ids: [...marcadas] },
      });
      toast('Etiquetas salvas');
      atualizar();
      fechar();
    } catch (e) {
      toast((e as Error).message, 'erro');
      setSalvando(false);
    }
  }

  return (
    <Modal titulo="Etiquetas" aoFechar={fechar}>
      <div className="etiquetas-lista" role="group" aria-label="Etiquetas da conversa">
        {todas.length === 0 && (
          <p className="nota-sec">Nenhuma etiqueta ainda. Crie a primeira abaixo.</p>
        )}
        {todas.map((t) => (
          <button
            key={t.id}
            className="etiqueta-opcao"
            aria-pressed={marcadas.has(t.id)}
            onClick={() => alternar(t.id)}
            style={{ '--etq': t.cor } as React.CSSProperties}
          >
            <span className="etq-ponto" />
            {t.nome}
            {marcadas.has(t.id) && <Check className="etq-check" />}
          </button>
        ))}
      </div>
      <div className="etiqueta-nova">
        <input
          id="etiqueta-nova"
          className="input"
          placeholder="Nova etiqueta"
          value={nova}
          maxLength={40}
          onChange={(e) => setNova(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void criar()}
        />
        <div className="etq-cores" role="radiogroup" aria-label="Cor">
          {CORES_ETIQUETA.map((k) => (
            <button
              key={k}
              role="radio"
              aria-checked={cor === k}
              aria-label={k}
              style={{ background: k }}
              onClick={() => setCor(k)}
            />
          ))}
        </div>
        <button className="btn" onClick={() => void criar()} disabled={!nova.trim()}>
          <Plus /> Criar
        </button>
      </div>
      <div className="acoes">
        <button className="btn" onClick={fechar}>
          Cancelar
        </button>
        <button className="btn primario" disabled={salvando} onClick={() => void salvar()}>
          Salvar
        </button>
      </div>
    </Modal>
  );
}

export function ModalBloquear({
  conversa: c,
  fechar,
}: {
  conversa: ConversaDetalheDTO;
  fechar: () => void;
}) {
  const toast = useToast();
  const atualizar = useAtualizar(c.id);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const bloqueado = c.contato.bloqueado;

  async function confirmar() {
    setSalvando(true);
    try {
      if (bloqueado) {
        await post(`/api/contatos/${c.contato.id}/desbloquear`);
        toast('Contato desbloqueado');
      } else {
        const r = await post<{ aviso: string | null }>(`/api/contatos/${c.contato.id}/bloquear`, {
          motivo: motivo.trim() || undefined,
        });
        toast(r.aviso ?? 'Contato bloqueado', r.aviso ? 'erro' : 'ok');
      }
      atualizar();
      fechar();
    } catch (e) {
      toast((e as Error).message, 'erro');
      setSalvando(false);
    }
  }

  return (
    <Modal titulo={bloqueado ? 'Desbloquear contato' : 'Bloquear contato'} aoFechar={fechar}>
      {bloqueado ? (
        <p style={{ margin: 0 }}>
          <b>{c.contato.nome}</b> volta a poder mandar mensagens, e você volta a poder responder.
        </p>
      ) : (
        <>
          <p style={{ margin: 0 }}>
            As mensagens de <b>{c.contato.nome}</b> passam a ser ignoradas e ninguém da equipe
            consegue responder. O número também é bloqueado no WhatsApp da empresa.
          </p>
          <label className="campo">
            <span>Motivo (opcional)</span>
            <input
              id="bloquear-motivo"
              className="input"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex: spam, trote"
              maxLength={300}
            />
          </label>
        </>
      )}
      <div className="acoes">
        <button className="btn" onClick={fechar}>
          Cancelar
        </button>
        <button
          className={`btn ${bloqueado ? 'primario' : 'perigo-cheio'}`}
          disabled={salvando}
          onClick={() => void confirmar()}
        >
          {bloqueado ? 'Desbloquear' : 'Bloquear'}
        </button>
      </div>
    </Modal>
  );
}

export function ModalApagar({
  conversa: c,
  fechar,
}: {
  conversa: ConversaDetalheDTO;
  fechar: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [salvando, setSalvando] = useState(false);
  const qtd = c.mensagens.filter((m) => m.autor !== 'sistema').length;

  async function apagar() {
    setSalvando(true);
    try {
      await api(`/api/conversas/${c.id}`, { metodo: 'DELETE' });
      qc.removeQueries({ queryKey: chaves.conversa(c.id) });
      qc.invalidateQueries({ queryKey: ['conversas'] });
      toast('Conversa apagada');
      fechar();
      navegar('/conversas', { replace: true });
    } catch (e) {
      toast((e as Error).message, 'erro');
      setSalvando(false);
    }
  }

  return (
    <Modal titulo="Apagar conversa" aoFechar={fechar}>
      <p style={{ margin: 0 }}>
        Apagar a conversa com <b>{c.contato.nome}</b>? As {qtd} mensagens, fotos, áudios e arquivos
        e o orçamento coletado somem de vez. <b>Não dá pra desfazer.</b>
      </p>
      <p className="nota-sec" style={{ margin: 0 }}>
        O contato continua cadastrado, e os pedidos dele não são apagados. No WhatsApp do cliente
        nada muda.
      </p>
      <div className="acoes">
        <button className="btn" onClick={fechar}>
          Cancelar
        </button>
        <button className="btn perigo-cheio" disabled={salvando} onClick={() => void apagar()}>
          Apagar conversa
        </button>
      </div>
    </Modal>
  );
}
